/**
 * Bulk Document Processor
 *
 * UI for processing multiple documents through the Universal Document Ingester.
 * Shows classification results, extracted entities, and processing status.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  FileText,
  FileSpreadsheet,
  Image,
  File,
  CheckCircle,
  XCircle,
  Loader2,
  Play,
  AlertTriangle,
  Building2,
  CreditCard,
  Receipt,
  FileCheck,
  Sparkles,
} from "lucide-react";

interface ProcessingResult {
  filename: string;
  success: boolean;
  classification?: {
    primary: string;
    format?: string;
    language?: string;
  };
  confidence?: number;
  transactionCount?: number;
  errors?: string[];
}

interface BulkDocumentProcessorProps {
  onProcessingComplete?: () => void;
}

const documentTypeIcons: Record<string, typeof FileText> = {
  bank_statement: FileSpreadsheet,
  invoice: Receipt,
  receipt: Receipt,
  tax_form: FileCheck,
  contract: FileText,
  unknown: File,
};

const documentTypeLabels: Record<string, string> = {
  bank_statement: "Bank Statement",
  invoice: "Invoice",
  receipt: "Receipt",
  tax_form: "Tax Form",
  contract: "Contract",
  payslip: "Payslip",
  annual_report: "Annual Report",
  unknown: "Unknown",
};

export function BulkDocumentProcessor({
  onProcessingComplete,
}: BulkDocumentProcessorProps) {
  const { selectedCompanyId } = useCompany();
  const [selectedDocs, setSelectedDocs] = useState<number[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ProcessingResult[]>([]);
  const [progress, setProgress] = useState(0);

  // Fetch inbox documents
  const {
    data: inboxDocs,
    isLoading: docsLoading,
    refetch,
  } = trpc.documentManagement.listByFolder.useQuery(
    {
      companyId: selectedCompanyId!,
      folder: "inbox",
      limit: 100,
    },
    { enabled: !!selectedCompanyId }
  );

  // Batch ingestion mutation
  const ingestBatchMutation = trpc.documentIngestion.ingestBatch.useMutation({
    onSuccess: data => {
      setResults(data.results);
      setProgress(100);
      setIsProcessing(false);
      refetch();
      onProcessingComplete?.();
    },
    onError: error => {
      setIsProcessing(false);
      setResults([
        {
          filename: "Batch Processing",
          success: false,
          errors: [error.message],
        },
      ]);
    },
  });

  // Get ingestion stats
  const { data: stats } = trpc.documentIngestion.getStats.useQuery(
    {
      companyId: selectedCompanyId!,
    },
    { enabled: !!selectedCompanyId }
  );

  const handleSelectAll = () => {
    if (selectedDocs.length === inboxDocs?.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(inboxDocs?.map(d => d.id) || []);
    }
  };

  const handleToggleDoc = (docId: number) => {
    setSelectedDocs(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const handleStartProcessing = () => {
    if (selectedDocs.length === 0 || !selectedCompanyId) return;

    setIsProcessing(true);
    setProgress(10);
    setResults([]);

    ingestBatchMutation.mutate({
      companyId: selectedCompanyId,
      documentIds: selectedDocs,
      skipLLM: false,
      concurrency: 3,
    });
  };

  const getFileIcon = (mimeType?: string, filename?: string) => {
    if (mimeType?.includes("csv") || filename?.endsWith(".csv")) {
      return FileSpreadsheet;
    }
    if (mimeType?.includes("pdf")) {
      return FileText;
    }
    if (mimeType?.includes("image")) {
      return Image;
    }
    return File;
  };

  const getConfidenceBadge = (confidence?: number) => {
    if (!confidence) return <Badge variant="outline">Unknown</Badge>;
    if (confidence >= 80)
      return <Badge className="bg-success text-white">{confidence}%</Badge>;
    if (confidence >= 50)
      return <Badge variant="secondary">{confidence}%</Badge>;
    return <Badge variant="outline">{confidence}%</Badge>;
  };

  if (!selectedCompanyId) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground text-center">
            Select a company to process documents
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && stats.totalDocuments > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.totalDocuments}</div>
              <p className="text-xs text-muted-foreground">Documents Indexed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">
                {stats.averageConfidence}%
              </div>
              <p className="text-xs text-muted-foreground">Avg Confidence</p>
            </CardContent>
          </Card>
          {stats.documentsByType
            ?.slice(0, 2)
            .map((item: { documentType: string; count: number }) => (
              <Card key={item.documentType}>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{item.count}</div>
                  <p className="text-xs text-muted-foreground">
                    {documentTypeLabels[item.documentType] || item.documentType}
                  </p>
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      {/* Document Selection */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                Process Documents
              </CardTitle>
              <CardDescription>
                Select documents from inbox for AI-powered classification and
                extraction
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                disabled={docsLoading || !inboxDocs?.length}
              >
                {selectedDocs.length === inboxDocs?.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
              <Button
                size="sm"
                onClick={handleStartProcessing}
                disabled={selectedDocs.length === 0 || isProcessing}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Process ({selectedDocs.length})
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isProcessing && (
            <div className="mb-4">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                Processing {selectedDocs.length} documents...
              </p>
            </div>
          )}

          {docsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !inboxDocs?.length ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No documents in inbox</p>
              <p className="text-sm">
                Upload documents to the File Inbox first
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[300px]">
              <div className="space-y-2">
                {inboxDocs.map(doc => {
                  const Icon = getFileIcon(doc.mimeType, doc.fileName);
                  return (
                    <div
                      key={doc.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                        selectedDocs.includes(doc.id)
                          ? "bg-[var(--primary)]/5 border-[var(--primary)]/30"
                          : "hover:bg-muted/50"
                      }`}
                      onClick={() => handleToggleDoc(doc.id)}
                    >
                      <Checkbox
                        checked={selectedDocs.includes(doc.id)}
                        onCheckedChange={() => handleToggleDoc(doc.id)}
                      />
                      <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {doc.originalName || doc.fileName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {doc.mimeType} •{" "}
                          {Math.round((doc.fileSize || 0) / 1024)}KB
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Processing Results */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5" />
              Processing Results
            </CardTitle>
            <CardDescription>
              {results.filter(r => r.success).length} of {results.length}{" "}
              documents processed successfully
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {results.map((result, idx) => {
                  const TypeIcon = result.classification?.primary
                    ? documentTypeIcons[result.classification.primary] || File
                    : File;

                  return (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border ${
                        result.success
                          ? "bg-success/5 border-[var(--success)]/20"
                          : "bg-destructive/5 border-destructive/20"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {result.success ? (
                          <CheckCircle className="h-5 w-5 text-success shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{result.filename}</p>
                            {result.classification && (
                              <Badge
                                variant="outline"
                                className="flex items-center gap-1"
                              >
                                <TypeIcon className="h-3 w-3" />
                                {documentTypeLabels[
                                  result.classification.primary
                                ] || result.classification.primary}
                              </Badge>
                            )}
                            {getConfidenceBadge(result.confidence)}
                          </div>

                          {result.success && (
                            <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                              {result.transactionCount !== undefined &&
                                result.transactionCount > 0 && (
                                  <span className="flex items-center gap-1">
                                    <CreditCard className="h-4 w-4" />
                                    {result.transactionCount} transactions
                                  </span>
                                )}
                              {result.classification?.language && (
                                <span className="flex items-center gap-1">
                                  Language:{" "}
                                  {result.classification.language.toUpperCase()}
                                </span>
                              )}
                            </div>
                          )}

                          {result.errors && result.errors.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {result.errors.map((error, i) => (
                                <p
                                  key={i}
                                  className="text-sm text-destructive flex items-start gap-1"
                                >
                                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                                  {error}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default BulkDocumentProcessor;
